
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/components/auth/AuthProvider";
import { useTransactionalEmail } from "@/hooks/use-transactional-email";

interface UseWaitlistProps {
  nhSlug: string;
  centerName: string;
  countySlug: string;
  email?: string;
}

export function useWaitlist({ nhSlug, centerName, countySlug, email }: UseWaitlistProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isInLeads, setIsInLeads] = useState(false);
  const { sendEmail } = useTransactionalEmail();

  // Check if the user has already joined the waitlist for this nursing home
  useEffect(() => {
    const checkLeads = async () => {
      if (user) {
        const { data: existingLeads, error } = await supabase
          .from('nursing_home_leads')
          .select('id')
          .eq('user_id', user.id)
          .eq('nursing_home_id', nhSlug)
          .limit(1);
        
        if (!error && existingLeads) {
          setIsInLeads(existingLeads.length > 0);
        }
      }
    };

    checkLeads();
  }, [user, nhSlug]);

  const handleJoinWaitlist = async () => {
    if (user) {
      try {
        if (isInLeads) {
          toast.info("Already in your leads", {
            description: "You've already joined the waitlist for this nursing home",
            action: {
              label: "View Dashboard",
              onClick: () => navigate("/dashboard")
            }
          });
          return;
        }
        
        // Insert the lead record and return the created lead
        const { data: leadData, error } = await supabase.from('nursing_home_leads').insert({
          user_id: user.id,
          nursing_home_id: nhSlug,
          nursing_home_name: centerName,
          location: countySlug,
          status: 'waitlist',
          notes: 'Added from nursing home sidebar'
        }).select();
        
        if (error) throw error;
        
        setIsInLeads(true);

        try {
          const claimLink = `${window.location.origin}/claim/${nhSlug}`;
          const leadId = leadData && leadData[0] ? leadData[0].id : undefined;
          
          // Send anonymized lead notification to nursing home
          await sendEmail({
            type: 'nursing_home_inquiry',
            data: {
              nursingHomeName: centerName,
              county: countySlug,
              message: "A qualified lead has joined your waitlist through Grandpal. They're actively looking to secure a place and would like to connect. Claim your profile to access this lead's contact details.",
              email: email || "hello@updates.grandpal.co",
              claimLink,
              leadId
            }
          });
          
          // Send confirmation email to the user
          await sendEmail({
            type: 'inquiry_confirmation',
            data: {
              firstName: user.user_metadata?.first_name || 'User',
              email: user.email || '',
              nursingHomeName: centerName,
              county: countySlug,
            }
          });
        } catch (emailError) {
          console.error("Failed to send nursing home inquiry email:", emailError);
          // Don't show an error toast since the main action succeeded
        }
        
        toast.success(`Added ${centerName} to your waitlist`, {
          description: "View all your saved nursing homes in your dashboard",
          action: {
            label: "View Dashboard",
            onClick: () => navigate("/dashboard")
          }
        });
      } catch (error) {
        console.error("Error adding to leads:", error);
        toast.error("Failed to add to your waitlist");
      }
    } else {
      // Persist nursing home context so it can be auto-added after signup/verification
      // Use localStorage so it survives email verification links opening in new tabs
      localStorage.setItem('pendingLeadNH', JSON.stringify({
        nhSlug,
        centerName,
        countySlug,
      }));
      navigate(`/auth/signup`);
      
      toast.info("Create a free account to save nursing homes", {
        description: `Sign up to add ${centerName} to your list and track your options.`
      });
    }
  };

  return {
    isInLeads,
    handleJoinWaitlist
  };
}
